import { useAtom } from '@reatom/npm-react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Button } from '@blade/ui-kit'
import {
  ChevronDownExactIcon,
  ChevronUpExactIcon,
  Details,
  FadeOverflow,
} from '@root/shared/ui-kit'

import { TradingAuditHeading } from './trading-audit-heading'
import { TradingAuditList } from './trading-audit-list'

import type { PairModel } from '@root/entities/pair'

type Props = {
  pair: PairModel
  onProceed?: () => void
  onNeverShowAgain?: () => void
  auditListMode?: 'permanent' | 'closeable'
  makerFilter?: string
}

export const TradingAudit: React.FC<Props> = ({
  pair,
  onProceed,
  onNeverShowAgain,
  auditListMode,
  makerFilter,
}) => {
  const { t } = useTranslation()

  const [audit] = useAtom(pair.audit)
  const withWarnings = audit.overAllChecks.overall !== 'Safe'

  return (
    <Root
      variant="vertical"
      css={`
        --button-border: ${withWarnings
          ? 'var(--error-7)'
          : 'var(--success-7)'};
        --button-background: ${withWarnings
          ? 'rgba(var(--error-7-rgb), 0.2)'
          : 'rgba(var(--success-7-rgb), 0.2)'};
      `}
    >
      <Outline>
        <Heading>
          <TradingAuditHeading audit={audit} />
          <Description>{t('trading.audit.description')}</Description>
          {onProceed ? (
            <ButtonProceed
              radius="rounded"
              variant="secondary"
              onClick={onProceed}
            >
              {t('trading.audit.button_proceed')}
            </ButtonProceed>
          ) : undefined}

          {onProceed ? (
            <Alert onClick={onNeverShowAgain}>
              {t('trading.audit.not_show_again')}
            </Alert>
          ) : undefined}
        </Heading>
        {auditListMode === 'closeable' ? (
          <Details
            summary={(renderProps) => (
              <Summary $isOpen={renderProps.isOpen}>
                {t('common.more_info')}
                {renderProps.isOpen ? (
                  <ChevronUpExactIcon width={9} height={9} />
                ) : (
                  <ChevronDownExactIcon width={9} height={9} />
                )}
              </Summary>
            )}
          >
            <TradingAuditList
              pair={pair}
              variant="full"
              makerFilter={makerFilter}
            />
          </Details>
        ) : (
          <TradingAuditList
            pair={pair}
            variant="full"
            makerFilter={makerFilter}
          />
        )}
      </Outline>
    </Root>
  )
}

const Root = styled(FadeOverflow)`
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  max-height: 100%;
  padding: 10px;
  background: var(--background-2);
`

const Outline = styled.div`
  box-shadow: inset 0 0 0 1px var(--background-2);

  display: grid;
  border-radius: 12px;
  gap: 16px;
`

const Heading = styled.header`
  padding: 20px 10px 0;
  display: grid;
  gap: 16px;
`

const Description = styled.p`
  color: var(--textA-8);

  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  text-align: center;

  padding-inline: 30px;
`

const ButtonProceed = styled(Button)`
  border: 1px solid var(--button-border);
  background: var(--button-background);

  cursor: pointer;

  height: 40px;
  flex-shrink: 0;

  color: var(--text-12);
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
`

const Alert = styled.p`
  color: var(--text-11);

  cursor: pointer;

  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  text-decoration-line: underline;
  text-decoration-style: dotted;
  text-decoration-skip-ink: auto;
  text-decoration-thickness: auto;
  text-underline-offset: 15%;
  text-underline-position: from-font;

  text-align: center;
`

const Summary = styled.div<{ $isOpen: boolean }>`
  padding: 10px 10px 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  font-weight: 600;
  font-size: 11px;
  color: ${(_) => (_.$isOpen ? 'var(--textA-12)' : 'var(--textA-11)')};
  transition: color 0.3s;
`
